import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { languageKeys } from "@op/puck-types";
import { useTranslations } from "../../../utils/translations";
import { ContentProvider, TranslationProvider } from "@op/utils";
import { createServerFn } from "@tanstack/react-start";
import { rendererClient } from "../../../lib/rendererClient";
import { TopicPage } from "@op/puck";
import { contextSchema, type Context } from "../../../utils/context";

const paramsSchema = z.object({
  languageKey: z.enum(languageKeys),
  topicSlug: z.string(),
});

const getTopic = createServerFn({ method: "GET" })
  .inputValidator(z.object({ params: paramsSchema, context: contextSchema }))
  .handler(
    async ({
      data: {
        params: { languageKey, topicSlug },
        context: { organizationSlug, projectSlug },
      },
    }) => {
      const res = await rendererClient.topics[":organizationSlug"][
        ":projectSlug"
      ][":languageKey"][":topicSlug"].$get({
        param: { organizationSlug, projectSlug, languageKey, topicSlug },
      });

      if (!res.ok) {
        if (res.status === 404) throw notFound();
        throw new Error("Failed to fetch topic");
      }

      const data = await res.json();
      return data;
    },
  );

export const Route = createFileRoute("/$languageKey/$topicSlug/")({
  params: paramsSchema,
  beforeLoad: ({ context: { languages }, params: { languageKey } }) => {
    return { languages: languages.filter((lang) => lang !== languageKey) };
  },
  loader: async ({ params, context }) =>
    await getTopic({ data: { params, context: context as Context } }),
  component: RouteComponent,
});

function RouteComponent() {
  const { languageKey, topicSlug } = Route.useParams();
  const { languages } = Route.useRouteContext();
  const {
    pages,
    imprintLink,
    privacyLink,
    copyrightText,
    hasImprint,
    hasPrivacyPolicy,
    heading,
    subheading,
    logoImage,
    organizationName,
    organizationLink,
  } = Route.useLoaderData();
  const translations = useTranslations();

  return (
    <ContentProvider
      citations={{ content: [], excursions: {} }}
      glossaryItems={[]}
      excursions={[]}
      versions={[]}
      outline={{ content: [], excursions: {} }}
      figures={{ content: [], excursions: {} }}
    >
      <TranslationProvider {...translations}>
        <TopicPage
          topicSlug={topicSlug}
          pages={pages}
          languageKey={languageKey}
          languages={languages}
          imprintLink={imprintLink ?? undefined}
          privacyLink={privacyLink ?? undefined}
          copyrightText={copyrightText ?? undefined}
          hasImprint={hasImprint}
          hasPrivacyPolicy={hasPrivacyPolicy}
          heading={heading ?? undefined}
          subheading={subheading ?? undefined}
          logoImage={logoImage ?? undefined}
          organizationName={organizationName ?? undefined}
          organizationLink={organizationLink ?? undefined}
          LinkComponent={Link}
        />
      </TranslationProvider>
    </ContentProvider>
  );
}
